import type { CodingStatus, TranscriptItem } from './coding-events';

type QuestionsItem = Extract<TranscriptItem, { kind: 'questions' }>;

/**
 * The interview's round cap, mirrored from the builder prompt's own rule
 * ("At most 3 rounds of questions, then you MUST present the brief." in
 * `src/coding/prompt.ts`). Pinned against that line by
 * `guided-interview-bar.drift.test.ts`, so the bar can never promise a
 * different number than the model was told.
 */
export const GUIDED_INTERVIEW_MAX_ROUNDS = 3;

/**
 * Pure — whether a card is one round of the guided INTERVIEW, as opposed to
 * a card that merely rides the interview's tag. The go/adjust gate is itself
 * tagged mode 'interview' (FRONT-917) and the consent card (FRONT-2340) is a
 * yes/no for an action; neither is a question the "stop asking" exit can end
 * — both survive a stop on purpose (FRONT-1786) — so neither counts.
 */
export function isInterviewRound(item: QuestionsItem): boolean {
  return item.mode === 'interview' && !item.confirmsBuild && !item.consentCard;
}

export interface GuidedInterviewBarState {
  /** The latest interview round — the batch the exit is sent against. */
  batchId: number;
  /** Whether that round is still waiting for the user. False between rounds
   *  (answered, next question not asked yet): the exit still works then, the
   *  route turns guided build off before it looks at the batch (FRONT-1160)
   *  and EDDY's next question is refused, so it asks in plain words instead. */
  open: boolean;
  /** 1-based: how many interview rounds have been asked so far. */
  round: number;
  /** The cap, raised to `round` if the model ever overruns it, so the bar
   *  never reads "round 4 of 3". */
  maxRounds: number;
}

const BRIEF_HEADING = '## Our build plan';

/**
 * Pure — FRONT-2478. When the Builder pins its "EDDY is interviewing you" bar
 * above the composer, and what it says. It stays up for the WHOLE interview,
 * including the gap after an answer while EDDY prepares the next round — a
 * bar that vanished on every answer and popped back with the next card moved
 * the layout under the user's pointer, and one such pop-in took a click meant
 * for the card (live, 2026-09-30). The interview is over, and the bar goes,
 * when any of these holds:
 *
 *  - guided build was stopped for the session, or the run failed;
 *  - the newest card is not an interview round (the go/adjust gate, a
 *    consent card or a build-mode fork has taken over);
 *  - EDDY has posted its build plan after the last round;
 *  - the last round is answered and no run is carrying the interview on;
 *  - the last round was dismissed (FRONT-2661): the dismiss ended its run,
 *    which leaves the tape "waiting", so the between-rounds rule above would
 *    keep promising a next question nobody is preparing. The bar comes back
 *    with EDDY's next interview round, since guided build is still on.
 *
 * An OPEN round keeps the bar even with the run wound down: the ten-minute
 * ask timeout leaves the card answerable.
 */
export function guidedInterviewBarState(input: {
  transcript: TranscriptItem[];
  guidedBuildStopped: boolean;
  runStatus: CodingStatus | undefined;
}): GuidedInterviewBarState | null {
  if (input.guidedBuildStopped || input.runStatus === 'failed') return null;
  let lastIndex = -1;
  for (let i = input.transcript.length - 1; i >= 0; i--) {
    if (input.transcript[i].kind === 'questions') {
      lastIndex = i;
      break;
    }
  }
  if (lastIndex === -1) return null;
  const last = input.transcript[lastIndex] as QuestionsItem;
  if (!isInterviewRound(last)) return null;
  for (const item of input.transcript.slice(lastIndex + 1)) {
    if (item.kind === 'assistant' && item.text.trimStart().startsWith(BRIEF_HEADING)) return null;
  }
  if (last.outcome === 'stopped' || last.outcome === 'dismissed') return null;
  const open = !last.answers;
  if (!open && input.runStatus !== 'working' && input.runStatus !== 'waiting') return null;
  let round = 0;
  for (const item of input.transcript) {
    if (item.kind === 'questions' && isInterviewRound(item)) round++;
  }
  return { batchId: last.batchId, open, round, maxRounds: Math.max(GUIDED_INTERVIEW_MAX_ROUNDS, round) };
}
